// Recovers a tab whose lazily-imported route chunks no longer match the build
// being served. Two shapes of the same problem:
//
//   - the chunk 404s, because a deploy replaced it
//   - the chunk loads, but links against a sibling chunk from a *different*
//     build, so an import has no matching export. Safari reports this as
//     "Importing binding name 'f' is not found.", Chrome as "does not provide
//     an export named". Minified export names differ per build, which is what
//     makes this the signature of a mixed-build module graph.
//
// A full page load fixes both: fresh HTML references one consistent set of
// chunks.
//
// The hard part is stopping. In CH-460 a provider's phone reloaded the class
// register about 1.5 times a second for minutes on end. The old guard was a
// 10-second cooldown that entry-client cleared on every successful render — but
// the render that counts as "successful" happens *before* the route chunk is
// imported, so every pass through the loop started with a clean guard and
// reloaded again. Hence the two rules below:
//
//   attempts are counted, not just timestamped, so a permanently mismatched
//   asset set gives up instead of reloading forever;
//
//   the count is only cleared by clearChunkReloadAttempts(), which entry-client
//   calls once the router's *initial route load* has settled without error —
//   i.e. once a lazy chunk has demonstrably imported and linked. A tab stuck in
//   the loop never reaches that, so its budget keeps draining; a tab that
//   genuinely recovered always does, so a later deploy gets a fresh budget.

const ATTEMPTS_KEY = 'chunk-reload-attempts';
// The old cooldown key. Cleared alongside the counter so tabs upgrading into
// this version don't carry a stale timestamp under a key we no longer read.
const LEGACY_COOLDOWN_KEY = 'chunk-reload';

// Both guards' keys, so the logout sweep in reducers.js can leave them alone —
// logging out must not hand a wedged tab a fresh reload budget.
export const CHUNK_RELOAD_STORAGE_KEYS = [ATTEMPTS_KEY, LEGACY_COOLDOWN_KEY];

const MAX_ATTEMPTS = 2;

// Reading sessionStorage throws outright — not returns null — when storage is
// blocked (Safari with cookies disabled), so every access goes through here.
const withStorage = (fn, fallback = undefined) => {
  try {
    return fn(sessionStorage);
  } catch {
    return fallback;
  }
};

export const isChunkLoadError = (message = '') =>
  // The chunk request itself failed.
  message.includes('Failed to fetch dynamically imported module') ||
  message.includes('error loading dynamically imported module') ||
  message.includes('Importing a module script failed') ||
  // The chunk loaded, but its imports don't line up with what was served.
  message.includes('Importing binding name') ||
  message.includes('does not provide an export named') ||
  message.includes('Unable to preload CSS');

// Where the router is trying to go, when a navigation is in flight. A data
// router only commits the new URL once the route's lazy import and loaders
// resolve — and that lazy import is exactly what fails here — so at reload time
// window.location still points at the page the user came *from*. Reloading that
// is what made CH-460 present as "tapping the register throws me back to the
// dashboard": the reload re-loaded /dashboard, never the register.
let pendingHref = null;

export const noteNavigationTarget = (href) => {
  pendingHref = href || null;
};

// One reload per document is always enough — the page is being torn down, so
// the rest of the error burst (preload error, import rejection, sibling chunks)
// has nothing left to add. Exposed so the splash gate can keep the splash up for
// the window between issuing the reload and the browser committing it.
let reloadIssued = false;
export const isChunkReloadInFlight = () => reloadIssued;

export const recoverFromChunkError = () => {
  if (reloadIssued) return false;

  const attempts = withStorage(
    (store) => Number.parseInt(store.getItem(ATTEMPTS_KEY), 10) || 0,
    null
  );
  if (attempts === null) {
    // With nowhere to count attempts there's no way to rule out a reload loop,
    // so don't start one. Same call util/appVersion.js makes for its own guard.
    console.error('[chunk-reload] chunk mismatch, but storage is blocked — not reloading');
    return false;
  }

  if (attempts >= MAX_ATTEMPTS) {
    // Deliberately an error, not a log: this is the line that tells us a tab
    // was stranded rather than silently recovered.
    console.error(
      `[chunk-reload] giving up after ${attempts} reloads — served assets look permanently mismatched`
    );
    return false;
  }

  const claimed = withStorage((store) => {
    store.setItem(ATTEMPTS_KEY, String(attempts + 1));
    return true;
  }, false);
  // Storage disappeared between the read and here; without the counter we can't
  // bound a loop, so don't reload.
  if (!claimed) return false;

  reloadIssued = true;
  const target = pendingHref;
  // eslint-disable-next-line no-console
  console.log(
    `[chunk-reload] reloading (attempt ${attempts + 1}/${MAX_ATTEMPTS})${
      target ? ` → ${target}` : ''
    }`
  );

  // assign() lands the user on the page they actually asked for. On a back or
  // forward navigation the browser has already moved the URL before the router
  // starts loading, so the target *is* where we are — reload() says that
  // plainly instead of relying on the same-URL rule that quietly turns assign()
  // into a replace.
  const isCurrentUrl =
    !!target && new URL(target, window.location.href).href === window.location.href;
  if (target && !isCurrentUrl) {
    window.location.assign(target);
  } else {
    window.location.reload();
  }
  return true;
};

// Called from entry-client once a route chunk has actually imported and linked,
// and from the manual retry on RouteErrorElement — the automatic recovery has
// spent its budget by the time that button is reachable, so a plain reload
// would fail the same way and land straight back on the error page.
export const clearChunkReloadAttempts = () => {
  withStorage((store) => {
    store.removeItem(ATTEMPTS_KEY);
    store.removeItem(LEGACY_COOLDOWN_KEY);
  });
};

// Test seam: module-level state has to be resettable between cases.
export const resetChunkReloadStateForTests = () => {
  pendingHref = null;
  reloadIssued = false;
};

export const chunkReloadConstantsForTests = {
  ATTEMPTS_KEY,
  LEGACY_COOLDOWN_KEY,
  MAX_ATTEMPTS,
};
